import { format, formatDistanceToNow, parseISO } from "date-fns";

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  try {
    return parseISO(value);
  } catch {
    return null;
  }
}

export function formatDate(
  value: string | Date | null | undefined,
  pattern = "MMM d, yyyy",
): string {
  const d = toDate(value);
  return d ? format(d, pattern) : "—";
}

export function formatDateTime(
  value: string | Date | null | undefined,
  pattern = "MMM d, yyyy HH:mm",
): string {
  const d = toDate(value);
  return d ? format(d, pattern) : "—";
}

export function formatRelativeDate(
  value: string | Date | null | undefined,
): string {
  const d = toDate(value);
  return d ? formatDistanceToNow(d, { addSuffix: true }) : "—";
}

export function formatCurrency(
  amount: number | null | undefined,
  currency = "EUR",
  locale = "fr-FR",
): string {
  if (amount == null || Number.isNaN(amount)) return "—";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
}

/** Compact EUR for slider labels — `1_500_000` → `"1.5M €"`,
 *  `50_000` → `"50K €"`, `500` → `"500 €"`. Returns `"—"` when null. */
export function formatEurCompact(
  value: number | null | undefined,
): string {
  if (value == null || !Number.isFinite(value)) return "—";
  const abs = Math.abs(value);
  if (abs >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, "")}M €`;
  }
  if (abs >= 1_000) {
    return `${Math.round(value / 1_000)}K €`;
  }
  return `${value} €`;
}

export function formatPercent(
  value: number | null | undefined,
  fractionDigits = 1,
): string {
  if (value == null || Number.isNaN(value)) return "—";
  // Backend returns rates as 0..1 by convention; if a value > 1 is passed,
  // assume it's already a percent and render as-is.
  const pct = value > 1 ? value : value * 100;
  return `${pct.toFixed(fractionDigits)}%`;
}

export function formatNumber(
  value: number | null | undefined,
  locale = "en-US",
): string {
  if (value == null || Number.isNaN(value)) return "—";
  return new Intl.NumberFormat(locale).format(value);
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null || Number.isNaN(seconds)) return "—";
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) return s ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm ? `${h}h ${rm}m` : `${h}h`;
}

export function initials(name: string | null | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}


/**
 * Pull a friendly city label out of an IANA timezone string.
 * "America/New_York" → "New York", "Europe/Paris" → "Paris", "UTC" → "UTC".
 */
export function formatTimezoneLabel(tz: string | null | undefined): string {
  if (!tz) return "";
  const trimmed = tz.trim();
  if (!trimmed) return "";
  const last = trimmed.split("/").pop() ?? trimmed;
  return last.replace(/_/g, " ");
}
